import React, { ReactElement } from 'react';
import styled from 'styled-components';
import Locale from './locale';
import { TLocaleISO } from '../types/locale';
import { TTheme } from '../types/styles/theme';

/**
 * Returns formatted currency amount based on language.
 * @function format
 * @param {TLocaleISO} isoLocale - ISO Locale.
 * @param {number} amount - Amount to format.
 * @param {number} decimals - Decimals to be shown
 * @return {string} text - Formatted amount.
 */
export const format = (isoLocale: TLocaleISO, amount: number, precision?: number): string => {
    const preciseAmount = amount.toFixed(typeof precision === 'number' ? precision : 2);
    if (Locale.isArabic(isoLocale)) {
        return `${preciseAmount} ${Locale.getCurrencyCode(isoLocale)}`;
    }
    return `${Locale.getCurrencyCode(isoLocale)} ${preciseAmount}`;
};

/**
 * Returns formatted currency amount based on language.
 * @function formatJSX
 * @param {TLocaleISO}      isoLocale       ISO Locale.
 * @param {number}          amount          Amount to format.
 * @param {number | null}   originalAmount  Amount to format.
 * @return {ReactElement}                   Formatted amount returned as a JSX element with span tags.
 */
export const formatJSX = (
    isoLocale: TLocaleISO,
    amount: number,
    originalAmount?: number | null,
    precision = 2,
): ReactElement => {
    const preciseAmount = amount.toFixed(typeof precision === 'number' ? precision : 2);

    // Sh*t's free yo
    if (!amount && !originalAmount) {
        return <Free>{Locale.isArabic(isoLocale) ? 'مجاني' : 'Free'}</Free>;
    }

    // Sh*t was expensive, now it's free yo
    if (!amount && !!originalAmount) {
        if (Locale.isArabic(isoLocale)) {
            return (
                <>
                    <Free>مجاني</Free>&nbsp;
                    {!!originalAmount && (
                        <>
                            <Strike className="original">{originalAmount}</Strike>&nbsp;
                        </>
                    )}
                    <span className="currency">{Locale.getCurrencyCode(isoLocale)}</span>
                </>
            );
        }
        return (
            <>
                <span className="currency">{Locale.getCurrencyCode(isoLocale)}</span>&nbsp;
                {!!originalAmount && (
                    <>
                        <Strike className="original">{originalAmount}</Strike>&nbsp;
                    </>
                )}
                <span className="value">{preciseAmount}</span>
                <Free>Free</Free>&nbsp;
            </>
        );
    }

    // Sh*t's not free, yo
    if (Locale.isArabic(isoLocale)) {
        return (
            <>
                <span className="value">{preciseAmount}</span>
                {!!originalAmount && (
                    <>
                        <Strike className="original">{originalAmount}</Strike>&nbsp;
                    </>
                )}
                <span className="currency">{Locale.getCurrencyCode(isoLocale)}</span>
            </>
        );
    }
    return (
        <>
            <span className="currency">{Locale.getCurrencyCode(isoLocale)}</span>&nbsp;
            {!!originalAmount && (
                <>
                    <Strike className="original">{originalAmount}</Strike>&nbsp;
                </>
            )}
            <span className="value">{preciseAmount}</span>
        </>
    );
};

const Strike = styled.s`
    color: ${({ theme }: { theme: TTheme }): string => theme.colors.bodyMid};
`;

const Free = styled.b`
    color: rgb(56, 102, 223);
    text-transform: uppercase;
`;

export const toUSD = (isoLocale: TLocaleISO, value: number): number => {
    let conversionFactor = 0;
    switch (Locale.getCurrencyCodeEN(isoLocale)) {
        case 'AED':
            conversionFactor = 1 / 3.67;
            break;
        case 'SAR':
            conversionFactor = 1 / 3.75;
            break;
        case 'EGP':
            conversionFactor = 1 / 16.23;
            break;
        default:
            conversionFactor = 1;
    }

    return Math.floor(conversionFactor * value) || 0;
};
